import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { MaskedLines } from "../components/Reveal";

const NEXT_STEPS = [
    {
        n: "01",
        t: "Check your email for the Square receipt",
        d: "Square emails your receipt automatically. Save it — it's your enrollment confirmation.",
    },
    {
        n: "02",
        t: "We open your file within 24 hours",
        d: "A specialist pulls your reports, runs a forensic audit and reaches out at the phone number you provided.",
    },
    {
        n: "03",
        t: "First disputes filed in 5 business days",
        d: "The first round of custom-drafted disputes goes out under FCRA §611 to all three bureaus.",
    },
    {
        n: "04",
        t: "Your portal is ready shortly after",
        d: "We'll email your login so you can track every letter, status update and score change — live.",
    },
];

export default function PaymentSuccess() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />

            <section className="pt-28 md:pt-36 pb-16 grain">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 items-end">
                    <div className="col-span-12 md:col-span-8">
                        <div className="flex items-center gap-3 mb-8 text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
                            <span className="w-8 h-px bg-muted-foreground" />
                            <CheckCircle2 size={14} className="text-accent" />
                            <span>Payment received · Enrollment complete</span>
                        </div>
                        <MaskedLines
                            as="h1"
                            className="font-editorial tracking-tighter text-[12vw] md:text-[8.4vw] lg:text-[7.2vw] leading-[0.86]"
                            lines={["You&rsquo;re", <><span className="italic text-accent">enrolled.</span></>]}
                        />
                    </div>
                    <div className="col-span-12 md:col-span-4 md:text-right">
                        <p className="text-base text-muted-foreground max-w-sm md:ml-auto leading-relaxed">
                            Welcome to Access. Here&rsquo;s what happens next.
                        </p>
                    </div>
                </div>
            </section>

            <section className="pb-24">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
                    {/* Receipt-style card */}
                    <div className="col-span-12 lg:col-span-4">
                        <div className="bg-foreground text-background p-8 md:p-10" data-testid="payment-receipt">
                            <div className="text-[10px] tracking-[0.24em] uppercase text-background/60">
                                Order summary
                            </div>
                            <div className="mt-4 border-t border-background/20 pt-4 font-mono-ui text-xs space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-background/60">Plan</span>
                                    <span>1-Year Membership</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-background/60">Total</span>
                                    <span>$500.00 USD</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-background/60">Guarantee</span>
                                    <span className="text-accent">+93 points</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-background/60">Processor</span>
                                    <span>Square</span>
                                </div>
                            </div>
                            <div className="mt-8">
                                <div className="text-[10px] tracking-[0.24em] uppercase text-background/60">
                                    Questions?
                                </div>
                                <a
                                    href="tel:5127831895"
                                    className="mt-2 block font-editorial text-2xl hover:text-accent"
                                >
                                    512.783.1895
                                </a>
                                <a
                                    href="mailto:accesscreditrepair@yahoo.com"
                                    className="mt-1 block font-mono-ui text-xs text-background/70 hover:text-accent break-all"
                                >
                                    accesscreditrepair@yahoo.com
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Next steps */}
                    <div className="col-span-12 lg:col-span-8">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-muted-foreground" />
                            What happens next
                        </div>
                        <div className="border-t hairline" data-testid="next-steps">
                            {NEXT_STEPS.map((s, i) => (
                                <motion.div
                                    key={s.n}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.7,
                                        delay: 0.1 + i * 0.08,
                                        ease: [0.76, 0, 0.24, 1],
                                    }}
                                    className="grid grid-cols-12 gap-6 border-b hairline py-8 items-baseline"
                                >
                                    <div className="col-span-2 font-mono-ui text-xs tracking-[0.2em] text-muted-foreground">
                                        {s.n}
                                    </div>
                                    <div className="col-span-10">
                                        <div className="font-editorial text-3xl md:text-4xl tracking-tight">
                                            {s.t}
                                        </div>
                                        <div className="mt-3 text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed">
                                            {s.d}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <Link
                                to="/"
                                data-testid="success-cta-home"
                                className="group inline-flex items-center gap-3 bg-foreground text-background px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-accent"
                            >
                                Back to home
                                <ArrowUpRight
                                    size={16}
                                    className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                />
                            </Link>
                            <a
                                href="tel:5127831895"
                                className="inline-flex items-center gap-3 border border-foreground px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                            >
                                Call your specialist · 512.783.1895
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
